// frontend/src/pages/client/BuyPointsClient.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import {
  ShoppingCartIcon,
  CubeIcon,
  ArchiveBoxIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  StarIcon,
  CheckIcon,
  ArrowPathIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  XMarkIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import './BuyPointsClient.css';

const ICON_COLORS = [
  { bg: '#eff6ff', icon: '#2563eb' },
  { bg: '#ecfeff', icon: '#0891b2' },
  { bg: '#f0fdf4', icon: '#16a34a' },
  { bg: '#fdf4ff', icon: '#9333ea' },
  { bg: '#fff7ed', icon: '#ea580c' },
  { bg: '#fef2f2', icon: '#dc2626' },
  { bg: '#f0fdf4', icon: '#059669' },
  { bg: '#fafaf9', icon: '#57534e' },
];
const getColor = (name) => ICON_COLORS[(name?.charCodeAt(0) || 0) % ICON_COLORS.length];

const PaymentResultModal = ({ result, onClose, onViewWallet }) => {
  const isSuccess = result.type === 'success';
  return (
    <motion.div
      className="prm-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        className="prm-modal"
        initial={{ opacity: 0, scale: 0.86, y: 28 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 16 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      >
        <button className="prm-close" onClick={onClose}>
          <XMarkIcon style={{ width: 16, height: 16 }} />
        </button>

        <motion.div
          className={`prm-icon-wrap prm-icon-wrap--${isSuccess ? 'success' : 'fail'}`}
          initial={{ scale: 0, rotate: -15 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 0.1 }}
        >
          {isSuccess
            ? <CheckCircleIcon style={{ width: 52, height: 52 }} />
            : <XCircleIcon style={{ width: 52, height: 52 }} />
          }
        </motion.div>

        <h2 className="prm-title">
          {isSuccess ? 'Paiement accepté !' : 'Paiement annulé'}
        </h2>
        <p className="prm-subtitle">
          {isSuccess
            ? 'Stripe a confirmé votre paiement. Les points seront crédités momentanément.'
            : "La transaction a été annulée. Aucun montant n'a été débité."}
        </p>

        {isSuccess && (result.pack_name || result.points || result.amount) && (
          <div className="prm-details">
            {result.service_name && (
              <div className="prm-row">
                <span>Service</span>
                <strong>{result.service_name}</strong>
              </div>
            )}
            {result.pack_name && (
              <div className="prm-row">
                <span>Pack</span>
                <strong>{result.pack_name}</strong>
              </div>
            )}
            {result.points && (
              <div className="prm-row">
                <span>Points ajoutés</span>
                <strong className="prm-pts">{Number(result.points).toLocaleString('fr-FR')} pts</strong>
              </div>
            )}
            {result.amount && (
              <div className="prm-row">
                <span>Montant payé</span>
                <strong>{result.amount} {result.currency || 'TND'}</strong>
              </div>
            )}
            <div className="prm-row">
              <span>Date</span>
              <strong>{new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
            </div>
          </div>
        )}

        <div className="prm-actions">
          {isSuccess ? (
            <>
              <motion.button className="prm-btn prm-btn--primary" onClick={onViewWallet} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <BanknotesIcon style={{ width: 16, height: 16 }} />
                Voir mon wallet
              </motion.button>
              <motion.button className="prm-btn prm-btn--ghost" onClick={onClose} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                Continuer les achats
              </motion.button>
            </>
          ) : (
            <motion.button className="prm-btn prm-btn--primary" onClick={onClose} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              Réessayer
            </motion.button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const stagger = { visible: { transition: { staggerChildren: 0.07 } } };
const fadeUp  = {
  hidden:  { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } }
};

const BuyPointsClient = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const timerRef  = useRef(null);
  const params    = new URLSearchParams(location.search);
  const preService = params.get('service');

  const [services, setServices]       = useState([]);
  const [walletMap, setWalletMap]     = useState({});   // { [serviceId]: wallet }
  const [expandedId, setExpandedId]   = useState(null);
  const [packsMap, setPacksMap]       = useState({});   // { [serviceId]: packs[] }
  const [loadingPacks, setLoadingPacks] = useState({}); // { [serviceId]: bool }
  const [loading, setLoading]         = useState(true);
  const [purchasing, setPurchasing]   = useState(null);
  const [msg, setMsg]                 = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);

  useEffect(() => {
    fetchInitialData();
    if (params.get('success') === '1') {
      const raw = localStorage.getItem('pending_payment');
      const data = raw ? JSON.parse(raw) : {};
      setPaymentModal({ type: 'success', ...data });
      if (raw) localStorage.removeItem('pending_payment');
      navigate(location.pathname, { replace: true });
    } else if (params.get('canceled') === '1') {
      setPaymentModal({ type: 'canceled' });
      navigate(location.pathname + (preService ? `?service=${preService}` : ''), { replace: true });
    }
    return () => clearTimeout(timerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showMsg = (type, text) => {
    setMsg({ type, text });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMsg(null), 5000);
  };

  const fetchInitialData = async () => {
    try {
      const [svcRes, walRes] = await Promise.all([
        API.get('/client/services/all'),
        API.get('/client/wallets'),
      ]);
      const svcs = svcRes.data.services || [];
      const wMap = {};
      (walRes.data.wallets || []).forEach(w => { wMap[w.company_id] = w; });
      setServices(svcs);
      setWalletMap(wMap);
      if (preService) {
        const id = parseInt(preService);
        setExpandedId(id);
        fetchPacksForService(id);
      }
    } catch { setServices([]); }
    finally { setLoading(false); }
  };

  const fetchPacksForService = async (serviceId) => {
    setLoadingPacks(p => ({ ...p, [serviceId]: true }));
    try {
      const res = await API.get(`/client/packs/${serviceId}`);
      setPacksMap(p => ({ ...p, [serviceId]: res.data.packs || [] }));
    } catch {
      setPacksMap(p => ({ ...p, [serviceId]: [] }));
    } finally {
      setLoadingPacks(p => ({ ...p, [serviceId]: false }));
    }
  };

  const toggleService = (serviceId) => {
    if (expandedId === serviceId) {
      setExpandedId(null);
    } else {
      setExpandedId(serviceId);
      if (!packsMap[serviceId]) fetchPacksForService(serviceId);
    }
  };

  const handlePurchase = async (pack) => {
    const service = services.find(s => s.id === expandedId);
    if (!service) return;
    setPurchasing(pack.id);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('pending_payment', JSON.stringify({
        pack_name:    pack.name,
        points:       pack.points_amount,
        amount:       pack.price,
        currency:     pack.currency || 'TND',
        service_name: service.name,
        timestamp:    Date.now(),
      }));
      const res = await API.post('/stripe/create-checkout-session', {
        pack_id:    pack.id,
        points:     pack.points_amount,
        amount:     pack.price,
        currency:   'eur',
        company_id: service.id,
        user_id:    user.id,
        partner_id: service.partner_id,
        type:       'client',
        success_url: `${window.location.origin}/dashboard/client/buy?success=1`,
        cancel_url:  `${window.location.origin}/dashboard/client/buy?service=${service.id}&canceled=1`,
      });
      if (res.data.url) window.location.href = res.data.url;
      else {
        localStorage.removeItem('pending_payment');
        showMsg('error', 'URL de paiement manquante');
      }
    } catch (err) {
      localStorage.removeItem('pending_payment');
      showMsg('error', err.response?.data?.error || 'Erreur lors du paiement');
    } finally { setPurchasing(null); }
  };

  if (loading) return (
    <div className="bp2-loading">
      <div className="bp2-spinner" />
      <p>Chargement…</p>
    </div>
  );

  return (
    <>
    <div className="bp2-page">

      {/* ── Hero header ── */}
      <motion.div
        className="bp2-hero"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      >
        <div className="bp2-hero-orb bp2-hero-orb-1" />
        <div className="bp2-hero-orb bp2-hero-orb-2" />

        <div className="bp2-hero-left">
          <div className="bp2-hero-icon">
            <ShoppingCartIcon style={{ width: 22, height: 22 }} />
          </div>
          <div>
            <h1>Acheter des points</h1>
            <p>Sélectionnez un service et choisissez votre pack</p>
          </div>
        </div>

        <div style={{ zIndex: 1 }}>
          <div className="bp2-stat-pill">
            <CubeIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
            <span>{services.length} service{services.length !== 1 ? 's' : ''} disponible{services.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </motion.div>

      {/* ── Alert ── */}
      <AnimatePresence>
        {msg && (
          <motion.div
            className={`bp2-alert bp2-alert--${msg.type}`}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          >
            {msg.type === 'success'
              ? <CheckCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              : <ExclamationCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
            }
            {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Services accordion list ── */}
      <motion.div
        className="bp2-services-wrap"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {services.length === 0 ? (
          <div className="bp2-empty">
            <ArchiveBoxIcon style={{ width: 40, height: 40 }} className="bp2-empty-icon" />
            <p>Aucun service disponible pour le moment</p>
          </div>
        ) : (
          services.map(svc => {
            const color   = getColor(svc.name);
            const wallet  = walletMap[svc.id];
            const isOpen  = expandedId === svc.id;
            const packs   = packsMap[svc.id] || [];
            const isLoadingPacks = loadingPacks[svc.id];

            return (
              <motion.div
                key={svc.id}
                className={`bp2-svc-card${isOpen ? ' bp2-svc-card--open' : ''}`}
                variants={fadeUp}
              >
                {/* Card header — always visible */}
                <div className="bp2-svc-card-header">
                  <div className="bp2-svc-icon-wrap" style={{ background: color.bg }}>
                    <CubeIcon style={{ width: 22, height: 22, color: color.icon }} />
                  </div>

                  <div className="bp2-svc-info">
                    <span className="bp2-svc-name">{svc.name}</span>
                    <span className="bp2-svc-partner">
                      <BuildingOfficeIcon style={{ width: 13, height: 13, flexShrink: 0 }} />
                      {svc.partner_name}
                    </span>
                    <span className="bp2-svc-meta">
                      {svc.packs_count || 0} pack{svc.packs_count !== 1 ? 's' : ''} · dès {svc.price_min} {svc.currency || 'TND'}
                    </span>
                  </div>

                  <div className="bp2-svc-right">
                    <div className="bp2-wallet-info">
                      <span className="bp2-wi-label">
                        <BanknotesIcon style={{ width: 12, height: 12, flexShrink: 0 }} /> Mon solde
                      </span>
                      <span className="bp2-wi-value">
                        {wallet ? `${Number(wallet.balance).toLocaleString()} pts` : '—'}
                      </span>
                    </div>

                    <motion.button
                      className={`bp2-expand-btn${isOpen ? ' bp2-expand-btn--open' : ''}`}
                      onClick={() => toggleService(svc.id)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <ShoppingCartIcon style={{ width: 15, height: 15, flexShrink: 0 }} />
                      Acheter des points
                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        <ChevronDownIcon style={{ width: 15, height: 15, flexShrink: 0 }} />
                      </motion.span>
                    </motion.button>
                  </div>
                </div>

                {/* Expandable packs section */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      className="bp2-packs-section"
                      key="packs"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className="bp2-packs-section-inner">
                        {isLoadingPacks ? (
                          <div className="bp2-packs-loading-inner">
                            <div className="bp2-spinner" />
                            <p>Chargement des packs…</p>
                          </div>
                        ) : packs.length === 0 ? (
                          <div className="bp2-empty-inline">
                            <ArchiveBoxIcon style={{ width: 28, height: 28 }} />
                            <p>Aucun pack disponible pour ce service</p>
                          </div>
                        ) : (
                          <>
                            <motion.div
                              className="bp2-packs-grid"
                              variants={stagger}
                              initial="hidden"
                              animate="visible"
                            >
                              {packs.map((pack, i) => {
                                const isPopular = packs.length > 1 && i === Math.floor(packs.length / 2);
                                return (
                                  <motion.div
                                    key={pack.id}
                                    className={`bp2-pack${isPopular ? ' bp2-pack--popular' : ''}`}
                                    variants={fadeUp}
                                    whileHover={{ y: -5, boxShadow: '0 20px 48px rgba(37,99,235,0.15)' }}
                                  >
                                    {isPopular && (
                                      <div className="bp2-popular-tag">
                                        <StarIcon style={{ width: 11, height: 11, flexShrink: 0 }} /> Populaire
                                      </div>
                                    )}

                                    <div className="bp2-pack-name">{pack.name}</div>

                                    <div className="bp2-pack-pts">
                                      <span className="bp2-pts-num">{Number(pack.points_amount).toLocaleString()}</span>
                                      <span className="bp2-pts-unit">pts</span>
                                    </div>

                                    <div className="bp2-pack-price">
                                      {pack.price} <span>{pack.currency}</span>
                                    </div>

                                    <div className="bp2-pack-rate">
                                      ≈ {((pack.price / pack.points_amount) * 100).toFixed(2)} {pack.currency} / 100 pts
                                    </div>

                                    <ul className="bp2-pack-features">
                                      <li><CheckIcon style={{ width: 12, height: 12, flexShrink: 0 }} /> Points valables 1 an</li>
                                      <li><CheckIcon style={{ width: 12, height: 12, flexShrink: 0 }} /> Paiement Stripe sécurisé</li>
                                      <li><CheckIcon style={{ width: 12, height: 12, flexShrink: 0 }} /> Crédit instantané</li>
                                    </ul>

                                    <motion.button
                                      className="bp2-buy-btn"
                                      onClick={() => handlePurchase(pack)}
                                      disabled={purchasing !== null}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.97 }}
                                    >
                                      {purchasing === pack.id
                                        ? <><ArrowPathIcon style={{ width: 16, height: 16 }} className="icon-spin" /> Redirection…</>
                                        : <><LockClosedIcon style={{ width: 15, height: 15, flexShrink: 0 }} /> Payer avec Stripe</>
                                      }
                                    </motion.button>
                                  </motion.div>
                                );
                              })}
                            </motion.div>

                            <div className="bp2-security">
                              <ShieldCheckIcon style={{ width: 15, height: 15, flexShrink: 0 }} />
                              <span>Paiement 100% sécurisé · Chiffrement SSL · Aucune donnée bancaire stockée</span>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })
        )}
      </motion.div>
    </div>

    <AnimatePresence>
      {paymentModal && (
        <PaymentResultModal
          result={paymentModal}
          onClose={() => { setPaymentModal(null); fetchInitialData(); }}
          onViewWallet={() => navigate('/dashboard/client/wallet')}
        />
      )}
    </AnimatePresence>
    </>
  );
};

export default BuyPointsClient;
